import Dexie, { type EntityTable } from 'dexie';
import type { Loan } from '@/types';
import type {
  BalanceCorrectionEvent,
  ChargeEvent,
  DisbursementEvent,
  EmiDateChangeEvent,
  PrepaymentEvent,
  RateChangeEvent,
} from '@/types';

/**
 * ============================================================================
 * LOCAL-FIRST STORAGE (IndexedDB via Dexie)
 * ============================================================================
 *
 * One object store per event type (rather than one polymorphic "events"
 * table) — chosen for straightforward per-type indexing (e.g. querying
 * all RateChangeEvents by effectiveDate) and simpler TypeScript typing at
 * the Dexie layer. src/lib/db/eventsRepo.ts provides a unified read API
 * over all stores for the Timeline UI, so this schema choice is not
 * visible to the rest of the app.
 *
 * Versioning: Dexie's `.version(n).stores(...)` mechanism is how schema
 * migrations are handled. This is now VERSION 2: the standalone
 * `emiTenureChangeEvents` table from version 1 has been removed (see the
 * Phase 1 correction that folded EMI/tenure changes into
 * RateChangeEvent, PrepaymentEvent, and BalanceCorrectionEvent instead —
 * src/types/events.ts). Per explicit product decision, no migration
 * machinery was added for this: Phase 1 has no production data yet, so
 * a clean version bump (which, for anyone who happened to already have a
 * version-1 database locally, simply drops that one now-unused table)
 * is acceptable. If this app already has real event data by the time a
 * future schema change is needed, that future change SHOULD include a
 * proper upgrade() migration — this clean-bump approach is a one-time
 * allowance for this pre-production correction only.
 */

class LoanTrackerDB extends Dexie {
  loans!: EntityTable<Loan, 'id'>;
  disbursementEvents!: EntityTable<DisbursementEvent, 'id'>;
  rateChangeEvents!: EntityTable<RateChangeEvent, 'id'>;
  prepaymentEvents!: EntityTable<PrepaymentEvent, 'id'>;
  balanceCorrectionEvents!: EntityTable<BalanceCorrectionEvent, 'id'>;
  chargeEvents!: EntityTable<ChargeEvent, 'id'>;
  emiDateChangeEvents!: EntityTable<EmiDateChangeEvent, 'id'>;

  constructor() {
    super('LicHflLoanTrackerDB');

    // Version 1 is intentionally not declared here. Dexie only needs the
    // latest schema declared when there's no real migration path to
    // preserve; declaring a stale version 1 that immediately gets
    // superseded would add nothing. See the version-2 note above.
    this.version(2).stores({
      // '&id' = primary key, unique. Other listed fields are indexed for
      // querying (e.g. by loanId, by effectiveDate, or filtering out
      // superseded rows via supersededAt).
      loans: '&id, slug, status',
      disbursementEvents: '&id, loanId, effectiveDate, supersededAt',
      rateChangeEvents: '&id, loanId, effectiveDate, supersededAt',
      prepaymentEvents: '&id, loanId, effectiveDate, supersededAt',
      balanceCorrectionEvents: '&id, loanId, effectiveDate, supersededAt',
      chargeEvents: '&id, loanId, effectiveDate, supersededAt',
      emiDateChangeEvents: '&id, loanId, effectiveDate, supersededAt',
    });
  }
}

export const db = new LoanTrackerDB();
